import { nanoid } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import i18next from 'i18next'
import type {
	CompanyQuoteTemplate,
	QuotesByTenantIdBasicQueryVariables,
} from 'src/API'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import { fetchQuoteInfo } from 'src/redux/slices/quotes'
import type {
	GetCompanyQueryApi,
	GetProjectsQueryApi,
	GetQuoteTotalsQueryApi,
	QuoteByTenantIdBasicArray,
	ReduxProjects,
	ReduxQuote,
	ReduxQuoteInfo,
	ReduxSectionRename,
	ReduxUserInfo,
} from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	checkIfItemIsAccessible,
	checkIfTemplateIsAccessible,
	fetchRecapInfoUtil,
} from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { delayInMs } from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { createInvoiceQuoteCostlines } from 'src/utils/quote/invoiceQuoteCostlines'
import { getQuoteVersion } from 'src/utils/quote/quoteUtil'
import { isRecapConditionalCheckError } from 'src/utils/recapGridUtils/isRecapConditionalCheckError'
import { tryCatch } from 'src/utils/tryCatch'
import { quoteTypes } from 'src/utils/typeUtils'

const tQuoteGridPage1 = i18next.getFixedT(null, 'QuoteGridPage1')
const tCompanyTemplate = i18next.getFixedT(null, 'CompanyTemplate')

export const quoteApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchQuoteById: builder.query<
			ReduxQuote,
			{ id: string; userInfo: ReduxUserInfo }
		>({
			queryFn: async ({
				id,
				userInfo,
			}: {
				id: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					const tQuoteGridPage1 = i18next.getFixedT(
						null,
						'QuoteGridPage1'
					)
					const {
						data: { getQuote: parsedQuote },
					} = (await API.graphql(
						graphqlOperation(queries.getQuote, { id })
					)) as { data: { getQuote: ReduxQuote } }
					// I will need to check if the quote is part of companyQuoteTemplate if so
					// i will check against the template instead
					const {
						data: {
							companyQuoteTemplateByQuoteId: {
								items: companyQuoteTemplateData,
							},
						},
					} = (await API.graphql(
						graphqlOperation(
							queries.companyQuoteTemplateByQuoteId,
							{
								quoteId: id,
								filter: {
									_deleted: {
										attributeExists: false,
									},
								},
							}
						)
					)) as {
						data: {
							companyQuoteTemplateByQuoteId: {
								items: CompanyQuoteTemplate[]
							}
						}
					}
					let accessibleQuote: ReduxQuote | null = null

					if (companyQuoteTemplateData?.length > 0) {
						const templateQuote = companyQuoteTemplateData[0]
						const accessible = checkIfTemplateIsAccessible({
							userInfo,
							item: templateQuote,
							subTeamIds: [],
						})
						if (accessible && parsedQuote) {
							accessibleQuote = parsedQuote
						}
					} else {
						accessibleQuote = checkIfItemIsAccessible(
							userInfo,
							parsedQuote
						)
					}
					if (!accessibleQuote) {
						makeErrorToast(tQuoteGridPage1('fetchQuoteError'))
						captureExceptionAndThrow(
							new Error("Quote doesn't exist"),
							{
								location: 'quoteApi.fetchQuoteById',
								requestedQuoteId: id,
								userId: userInfo.id,
								requestedTenantId: parsedQuote?.tenantId,
							}
						)
					}
					return { data: accessibleQuote }
				} catch (error) {
					return { error }
				}
			},
			providesTags: (result, error, { id }) => [{ type: 'Quote', id }],
		}),
		// Fetches quote by parent Project Id
		fetchQuotesByProjectId: builder.query({
			query: ({
				projectId,
				filter,
			}: {
				projectId: string
				filter: any
			}) => ({
				queryName: 'quotesByParentProjectId',
				data: {
					parentProjectId: projectId,
					filter,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxQuote[]) => response,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'Quote' as const,
								id: id as string,
							})),
							{ type: 'Quote', id: 'LIST' },
						]
					: [{ type: 'Quote', id: 'LIST' }],
		}),
		// Fetches quote by tenantID
		fetchQuotesByTenantIdBasic: builder.query({
			query: ({
				tenantId,
				filter,
			}: {
				tenantId: string
				filter: QuotesByTenantIdBasicQueryVariables['filter']
			}) => ({
				queryName: 'quotesByTenantIdBasic',
				apiName: 'quotesByTenantId',
				data: {
					tenantId,
					filter,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: QuoteByTenantIdBasicArray | null) =>
				response,
			providesTags: (result) =>
				result && result.length > 0 // add a type guard to check if result is not null and if it has a length greater than 0
					? [
							...result.map(({ id }) => ({
								type: 'Quote' as const,
								id: id as string,
							})),
							{ type: 'Quote', id: 'LIST' },
						]
					: [{ type: 'Quote', id: 'LIST' }],
		}),
		//Fetch quote templates by tenantId
		fetchCompanyQuoteTemplatesByTenantId: builder.query({
			queryFn: async ({
				tenantId,
				userInfo,
			}: {
				tenantId: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					const {
						data: {
							companyQuoteTemplateByTenantId: {
								items: companyQuoteTemplateData,
							},
						},
					} = (await API.graphql(
						graphqlOperation(
							queries.companyQuoteTemplateByTenantId,
							{
								tenantId,
								filter: {
									_deleted: {
										attributeExists: false,
									},
								},
							}
						)
					)) as {
						data: {
							companyQuoteTemplateByTenantId: {
								items: CompanyQuoteTemplate[]
							}
						}
					}
					console.log('userInfo.teamIds: ', userInfo.teamIds)
					console.log(
						'companyQuoteTemplateData teamIds: ',
						companyQuoteTemplateData
					)
					const freelancerSubTeamIds: string[] = []
					if (userInfo.cognitoGroup?.includes('freelancer')) {
						const { data: projectData, error: projectError } =
							await tryCatch(
								API.graphql(
									graphqlOperation(
										queries.projectsByTenantId,
										{
											tenantId,
											filter: {
												activeFlag: { eq: true },
												freelancers: {
													contains: userInfo.tenantId,
												},
											},
										}
									)
								) as Promise<{
									data: {
										projectsByTenantId: {
											items: ReduxProjects[]
										}
									}
								}>
							)
						if (projectError) {
							const fetchProjectsError = new Error(
								'Error fetching freelancer projects',
								{ cause: projectError }
							)
							captureExceptionAndThrow(fetchProjectsError, {
								location:
									'quoteApi.fetchBasicCompanyQuoteTemplates',
								requestedTenantId: tenantId,
								userId: userInfo.id,
								errorMessage: projectError?.message,
							})
						}
						const projectArray =
							projectData?.data.projectsByTenantId.items
						const projectSubTeamIds = projectArray?.map(
							(item) => item.parentSubTeamId
						)
						const uniqueProjectSubTeamIds = [
							...new Set(projectSubTeamIds),
						].filter(
							(id): id is string =>
								id !== null && id !== undefined
						)
						freelancerSubTeamIds.push(...uniqueProjectSubTeamIds)
					}

					const filteredCompanyQuoteTemplate =
						companyQuoteTemplateData.filter(
							(item) =>
								!!checkIfTemplateIsAccessible({
									userInfo,
									item,
									subTeamIds: freelancerSubTeamIds,
								})
						)
					const quotePromise = [] as Promise<any>[]
					for (const item of filteredCompanyQuoteTemplate) {
						quotePromise.push(
							API.graphql(
								graphqlOperation(queries.getQuote, {
									id: item.quoteId,
								})
							) as Promise<any>
						)
					}
					const results = await Promise.allSettled(quotePromise)
					const quoteData = results
						.filter(
							(
								result
							): result is PromiseFulfilledResult<{
								data: { getQuote: ReduxQuote }
							}> => result.status === 'fulfilled'
						)
						.map((result) => result.value.data.getQuote)
					return {
						data: {
							quoteData: quoteData,
							companyQuoteTemplateData:
								filteredCompanyQuoteTemplate,
						},
					}
				} catch (error) {
					return { error }
				}
			},
			providesTags: (result) =>
				result
					? [
							...result.quoteData.map((r: ReduxQuote) => ({
								type: 'QuoteTemplate' as const,
								id: r.id as string,
							})),
							{ type: 'QuoteTemplate', id: 'LIST' },
						]
					: [{ type: 'QuoteTemplate', id: 'LIST' }],
		}),
		fetchBasicCompanyQuoteTemplates: builder.query({
			queryFn: async ({
				tenantId,
				userInfo,
			}: {
				tenantId: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					const {
						data: {
							companyQuoteTemplateByTenantId: {
								items: companyQuoteTemplateData,
							},
						},
					} = (await API.graphql(
						graphqlOperation(
							queries.companyQuoteTemplateByTenantId,
							{
								tenantId,
								filter: {
									_deleted: {
										attributeExists: false,
									},
								},
							}
						)
					)) as {
						data: {
							companyQuoteTemplateByTenantId: {
								items: CompanyQuoteTemplate[]
							}
						}
					}
					const filteredCompanyQuoteTemplate =
						companyQuoteTemplateData.filter(
							(item) =>
								!!checkIfTemplateIsAccessible({
									userInfo,
									item,
									subTeamIds: [],
								})
						)
					return {
						data: {
							companyQuoteTemplateData:
								filteredCompanyQuoteTemplate,
						},
					}
				} catch (error) {
					return { error }
				}
			},
			providesTags: (result) =>
				result
					? [
							...result.companyQuoteTemplateData.map(
								(r: CompanyQuoteTemplate) => ({
									type: 'BasicQuoteTemplate' as const,
									id: r.quoteId as string,
								})
							),
							{ type: 'BasicQuoteTemplate', id: 'LIST' },
						]
					: [{ type: 'BasicQuoteTemplate', id: 'LIST' }],
		}),
		// Fetch Quotes that have the childFreelancer
		fetchChildFreelancerQuotes: builder.query({
			query: ({
				currentCompanyTenantId,
				freelancerTenantId,
			}: {
				currentCompanyTenantId: string
				freelancerTenantId: string
			}) => ({
				queryName: 'quotesByTenantId',
				data: {
					tenantId: currentCompanyTenantId,
					filter: {
						activeFlag: { eq: true },
						childFreelancers: { contains: freelancerTenantId },
					},
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxQuote[]) => {
				return response
			},
			providesTags: (result) =>
				result
					? [
							...result.map((r: ReduxQuote) => ({
								type: 'Quote' as const,
								id: r.id as string,
							})),
							{ type: 'Quote', id: 'LIST' },
						]
					: [{ type: 'Quote', id: 'LIST' }],
		}),
		fetchQuoteTotalsById: builder.query({
			query: (quoteId: string) => ({
				queryName: 'getQuoteTotals',
				data: {
					quoteId,
				},
				isList: false,
			}),
			transformResponse: (
				response: GetQuoteTotalsQueryApi['getQuoteTotals']
			) => {
				const typedResponse = response
				return typedResponse
			},
			providesTags: (result, error, quoteId) => [
				{ type: 'QuoteTotals', id: quoteId },
			],
		}),
		// Creates a quote that is required by PreparePurchase that doesn't have an associated costline id
		createInvoiceQuote: builder.mutation({
			queryFn: async ({
				quoteId,
				projectId,
			}: {
				quoteId: string
				projectId: string
			}) => {
				try {
					//We fetch the project Data and then create the new quote
					const {
						data: { getProjects: projectData },
					} = (await API.graphql(
						graphqlOperation(queries.getProjects, {
							id: projectId,
						})
					)) as { data: GetProjectsQueryApi }

					let quoteType = projectData?.projectType as QuoteType
					const tenantId = projectData?.tenantId
					if (!projectData) {
						captureExceptionAndThrow(
							new Error(`Project doesn't exist`),
							{
								location: 'quoteApi.createInvoiceQuote',
								targetProjectId: projectId,
								requestedQuoteId: quoteId,
							}
						)
					}
					if (!quoteType && tenantId) {
						const {
							data: { getCompany: companyData },
						} = (await API.graphql(
							graphqlOperation(queries.getCompany, {
								id: tenantId,
							})
						)) as { data: GetCompanyQueryApi }
						quoteType = companyData?.defaultProjectType as QuoteType
					}
					if (!quoteType) {
						Sentry.captureException(
							new Error(
								`Project type is not correct projectData: ${projectData}`
							)
						)
						captureExceptionAndThrow(
							new Error(`Project type doesn't exist`),
							{
								location: 'quoteApi.createInvoiceQuote',
								targetProjectId: projectId,
								requestedQuoteId: quoteId,
							}
						)
					}
					if (!quoteTypes?.includes(quoteType)) {
						Sentry.captureException(
							new Error(
								`Project type is not correct projectData: ${projectData}`
							)
						)
						captureExceptionAndThrow(
							new Error('Project type is not correct'),
							{
								location: 'quoteApi.createInvoiceQuote',
								targetProjectId: projectId,
								requestedQuoteId: quoteId,
								projectType: quoteType,
							}
						)
					}
					// We input the basic values of a quote
					const newQuote = {
						id: quoteId,
						parentProjectId: projectData.id,
						name: `Invoice Quote for ${projectData.name}`,
						type: 'invoiceQuote',
						parentTeamId: projectData.parentTeamId,
						parentSubTeamId: projectData.parentSubTeamId,
						quoteType: quoteType,
						quoteVersion: getQuoteVersion(quoteType),
						tenantId: projectData.tenantId,
						freelancers: ['dummyId'],
						childFreelancers: [
							...(projectData.childFreelancers || ['dummyId']),
						],
						offlineIds: ['dummyId'],
						activeFlag: true,
						billLocation: 'France',
						marginPercentage: 15,
						overheadPercentage: 10,
						exchangeRate: 1,
						isStarred: true,
					} as ReduxQuote

					const {
						data: { createQuote },
					} = (await API.graphql(
						graphqlOperation(mutations.createQuote, {
							input: newQuote,
						})
					)) as {
						data: { createQuote: ReduxQuote }
					}
					// We wait for 1 second to make sure that the quote is created
					await delayInMs(1000)
					if (!createQuote) {
						captureExceptionAndThrow(
							new Error('Error in creating new quote'),
							{
								location: 'quoteApi.createInvoiceQuote',
								requestedQuoteId: quoteId,
								targetProjectId: projectId,
							}
						)
					}
					// We will now create the total costlines for all category and subCategory based on quote type
					const newCostlines = createInvoiceQuoteCostlines({
						quoteDetails: createQuote,
					})
					const createCostlinePromiseArray = newCostlines?.map(
						async (costline) => {
							try {
								await API.graphql(
									graphqlOperation(
										mutations.createRecapInfo,
										{
											input: costline,
										}
									)
								)
							} catch (createErr) {
								// Deterministic recap ids make a collision the
								// expected sign that another creator already
								// created this row. Swallow it so the
								// rollback-by-wipe catch below only fires on
								// genuine errors.
								if (!isRecapConditionalCheckError(createErr))
									throw createErr
							}
						}
					)
					try {
						// Checks if the createCostlinePromiseArray are not undefined
						createCostlinePromiseArray &&
							(await Promise.all(createCostlinePromiseArray))
					} catch (error) {
						const tCompanyTemplate = i18next.getFixedT(
							null,
							'companyTemplate'
						)
						// Catch blocks deletes all recap costlines that were accidentally created
						const recapInfoCostlines = await fetchRecapInfoUtil(
							quoteId,
							{
								activeFlag: { eq: true },
							}
						)
						recapInfoCostlines.map(async (item) => {
							await API.graphql(
								graphqlOperation(mutations.deleteRecapInfo),
								{
									input: item.id,
								}
							)
						})
						captureExceptionAndThrow(
							new Error('Error Creating Costline'),
							{
								location: 'quoteApi.createInvoiceQuote',
								recapQuoteId: quoteId,
								recapErrorMessage: getErrorMessage(error),
							}
						)
					}
					return { data: createQuote }
				} catch (error: any) {
					console.error('error: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Quote', id: 'LIST' }],
		}),
		createCompanyTemplateQuote: builder.mutation({
			queryFn: async ({ quoteId }: { quoteId: string }) => {
				try {
					await API.graphql(
						graphqlOperation(
							mutations.createNewCompanyQuoteTemplate,
							{
								quoteId,
							}
						)
					)
					makeSuccessToast(
						tCompanyTemplate('createCompanyTemplateSuccess')
					)
					return { data: quoteId }
				} catch (error) {
					console.error('error: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: ['QuoteTemplate', 'BasicQuoteTemplate'],
		}),
		updateCompanyTemplateQuote: builder.mutation({
			queryFn: async ({
				companyQuoteTemplateId,
				teamIds,
				subTeamIds,
			}: {
				companyQuoteTemplateId: string
				teamIds: string[]
				subTeamIds: string[]
			}) => {
				try {
					const {
						data: {
							getCompanyQuoteTemplate: companyQuoteTemplateData,
						},
					} = (await API.graphql(
						graphqlOperation(queries.getCompanyQuoteTemplate, {
							id: companyQuoteTemplateId,
						})
					)) as {
						data: { getCompanyQuoteTemplate: CompanyQuoteTemplate }
					}
					const updateParams = {
						id: companyQuoteTemplateId,
						teamIds,
						subTeamIds,
					}
					const {
						data: { updateCompanyQuoteTemplate },
					} = (await API.graphql(
						graphqlOperation(mutations.updateCompanyQuoteTemplate, {
							input: updateParams,
						})
					)) as {
						data: {
							updateCompanyQuoteTemplate: CompanyQuoteTemplate
						}
					}
					makeSuccessToast(
						tCompanyTemplate('updateCompanyTemplateSuccess')
					)
					return { data: updateCompanyQuoteTemplate }
				} catch (error) {
					console.error('error: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: ['QuoteTemplate', 'BasicQuoteTemplate'],
		}),
		deleteCompanyTemplateQuote: builder.mutation({
			queryFn: async ({
				quoteId,
				companyQuoteTemplateId,
			}: {
				quoteId: string
				companyQuoteTemplateId: string
			}) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.deleteQuoteById, {
							quoteId,
						})
					)
					const {
						data: {
							getCompanyQuoteTemplate: companyQuoteTemplateData,
						},
					} = (await API.graphql(
						graphqlOperation(queries.getCompanyQuoteTemplate, {
							id: companyQuoteTemplateId,
						})
					)) as {
						data: { getCompanyQuoteTemplate: CompanyQuoteTemplate }
					}
					if (companyQuoteTemplateData) {
						await API.graphql(
							graphqlOperation(
								mutations.deleteCompanyQuoteTemplate,
								{
									input: {
										id: companyQuoteTemplateId,
									},
								}
							)
						)
					}
					makeSuccessToast(tCompanyTemplate('deleteTemplateSuccess'))
					return { data: quoteId }
				} catch (error) {
					console.error('error: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'QuoteTemplate', id: 'LIST' }],
		}),
		createSectionRename: builder.mutation({
			queryFn: async (
				{
					quoteId,
					sectionId,
					sectionType,
					tenantId,
					sectionName,
					sectionName_fr,
					sectionTotalRow,
				}: {
					quoteId: string
					sectionId: string
					sectionType: string
					tenantId: string
					sectionName?: string | null
					sectionName_fr?: string | null
					sectionTotalRow?: ReduxQuoteInfo
				},
				{ dispatch }
			) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.createSectionRename, {
							input: {
								id: nanoid(),
								quoteId,
								sectionId,
								sectionType,
								tenantId,
								sectionName,
								sectionName_fr,
							},
						})
					)
					if (sectionTotalRow) {
						const data = (await API.graphql(
							graphqlOperation(queries.getQuoteInfo, {
								id: sectionTotalRow.id,
							})
						)) as {
							data: { getQuoteInfo: ReduxQuoteInfo }
						}
						let sectionPrefix = ''
						if (
							sectionTotalRow.costLineType === 'subCategoryTotal'
						) {
							sectionPrefix = `Total - ${sectionTotalRow.subCategoryPrefix}`
						} else if (
							sectionTotalRow.costLineType === 'categoryTotal'
						) {
							sectionPrefix = `Total - ${sectionTotalRow.categoryPrefix}`
						}
						const updateParams = {
							id: sectionTotalRow.id,
							item: `${sectionPrefix}: ${sectionName}`,
							item_fr: `${sectionPrefix}: ${sectionName_fr}`,
						}
						await API.graphql(
							graphqlOperation(mutations.updateQuoteInfo, {
								input: updateParams,
							})
						)
					}
					// Call redux thunk to fetch quoteInfo again
					await dispatch(fetchQuoteInfo({ quoteId }))

					makeSuccessToast(
						tQuoteGridPage1('createSectionRenameSuccess')
					)
					return { data: quoteId }
				} catch (error) {
					console.error('error in createSectionRename: ', error)
					makeErrorToast(tQuoteGridPage1('createSectionRenameError'))
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'QuoteInfo', id: 'LIST' }],
		}),
		updateSectionRename: builder.mutation({
			queryFn: async (
				{
					sectionRenameId,
					sectionName,
					sectionName_fr,
					quoteId,
					sectionTotalRow,
				}: {
					sectionRenameId: string
					sectionName: string
					sectionName_fr: string
					quoteId: string
					sectionTotalRow?: ReduxQuoteInfo
				},
				{ dispatch }
			) => {
				try {
					const data = (await API.graphql(
						graphqlOperation(queries.getSectionRename, {
							id: sectionRenameId,
						})
					)) as { data: { getSectionRename: ReduxSectionRename } }

					const updateParams = {
						id: sectionRenameId,
						sectionName,
						sectionName_fr,
					}
					await API.graphql(
						graphqlOperation(mutations.updateSectionRename, {
							input: updateParams,
						})
					)
					if (sectionTotalRow) {
						const data = (await API.graphql(
							graphqlOperation(queries.getQuoteInfo, {
								id: sectionTotalRow.id,
							})
						)) as {
							data: { getQuoteInfo: ReduxQuoteInfo }
						}
						let sectionPrefix = ''
						if (
							sectionTotalRow.costLineType === 'subCategoryTotal'
						) {
							sectionPrefix = `Total - ${sectionTotalRow.subCategoryPrefix}`
						} else if (
							sectionTotalRow.costLineType === 'categoryTotal'
						) {
							sectionPrefix = `Total - ${sectionTotalRow.categoryPrefix}`
						}
						const updateParams = {
							id: sectionTotalRow.id,
							item: `${sectionPrefix}: ${sectionName}`,
							item_fr: `${sectionPrefix}: ${sectionName_fr}`,
						}
						await API.graphql(
							graphqlOperation(mutations.updateQuoteInfo, {
								input: updateParams,
							})
						)
					}
					await dispatch(fetchQuoteInfo({ quoteId }))

					makeSuccessToast(
						tQuoteGridPage1('updateSectionRenameSuccess')
					)
					return { data: quoteId }
				} catch (error) {
					console.error('error in updateSectionRename: ', error)
					makeErrorToast(tQuoteGridPage1('updateSectionRenameError'))
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'QuoteInfo', id: 'LIST' }],
		}),
		fetchSectionRename: builder.query({
			query: ({ quoteId }: { quoteId: string }) => ({
				queryName: 'sectionRenameByQuoteId',
				data: {
					quoteId,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxSectionRename[]) => {
				const sectionRenameDict = Object.fromEntries(
					response.map((i) => [i.sectionId, i])
				)
				return sectionRenameDict
			},
			providesTags: [{ type: 'SectionRename', id: 'LIST' }],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchQuoteByIdQuery,
	useFetchQuotesByProjectIdQuery,
	useFetchQuotesByTenantIdBasicQuery,
	useFetchCompanyQuoteTemplatesByTenantIdQuery,
	useFetchBasicCompanyQuoteTemplatesQuery,
	useFetchChildFreelancerQuotesQuery,
	useFetchQuoteTotalsByIdQuery,
	useCreateInvoiceQuoteMutation,
	useCreateCompanyTemplateQuoteMutation,
	useUpdateCompanyTemplateQuoteMutation,
	useDeleteCompanyTemplateQuoteMutation,
	useCreateSectionRenameMutation,
	useUpdateSectionRenameMutation,
	useFetchSectionRenameQuery,
} = quoteApi
